// Lista ticket con filtri avanzati, ricerca e paginazione
import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { Plus, Filter, Search } from 'lucide-react'
import type { TicketStatus, TicketPriority, Ticket } from '@fieldcore/types'
import { PageHeader } from '../../components/layout/PageHeader'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { Select } from '../../components/ui/Select'
import { Table } from '../../components/ui/Table'
import { StatusPill } from '../../components/ui/StatusPill'
import { PriorityIndicator } from '../../components/ui/PriorityIndicator'
import { SLAIndicator } from './components/SLAIndicator'
import { ContextualHelp } from '../../components/shared/ContextualHelp'
import { useTicketsList } from '../../hooks/useTickets'
import { formatDate, formatRelativeDate } from '../../lib/utils'
import { useAuth } from '../../hooks/useAuth'

const STATUS_OPTIONS = [
  { value: 'all', label: 'Attivi (esclusi completati)' },
  { value: 'new', label: 'Nuovo' },
  { value: 'assigned', label: 'Assegnato' },
  { value: 'in_progress', label: 'In lavorazione' },
  { value: 'escalated', label: 'Escalato' },
  { value: 'paused', label: 'In pausa' },
  { value: 'completed', label: 'Completati' },
  { value: 'cancelled', label: 'Annullati' },
]

// Stati attivi da escludere quando si visualizzano "tutti"
const ACTIVE_STATUSES = ['new', 'assigned', 'in_progress', 'escalated', 'paused']

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Tutte le priorità' },
  { value: 'critical', label: 'Critica' },
  { value: 'high', label: 'Alta' },
  { value: 'medium', label: 'Media' },
  { value: 'low', label: 'Bassa' },
]

export function TicketsListPage() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { user } = useAuth()
  const isTechnician = user?.role === 'technician'
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState(searchParams.get('status') ?? 'all')
  const [priority, setPriority] = useState('all')
  const [page, setPage] = useState(1)

  const { data, isLoading } = useTicketsList({
    search: search || undefined,
    // "all" = filtro attivi lato server; altrimenti passa lo status specifico
    status: status !== 'all' ? (status as TicketStatus) : undefined,
    excludeCompleted: status === 'all',
    priority: priority !== 'all' ? priority : undefined,
    page,
    limit: 20,
  })

  const columns = [
    {
      key: 'number',
      header: '#',
      cell: (ticket: Ticket) => (
        <span className="font-mono text-xs text-[var(--fc-foreground-muted)]">
          {ticket.ticketNumber}
        </span>
      ),
      className: 'w-24',
    },
    {
      key: 'title',
      header: 'Titolo',
      cell: (ticket: Ticket) => (
        <div>
          <p className="font-medium text-[var(--fc-foreground)]">{ticket.title}</p>
          {ticket.description && (
            <p className="text-xs text-[var(--fc-foreground-muted)] truncate max-w-xs">
              {ticket.description}
            </p>
          )}
        </div>
      ),
    },
    {
      key: 'company',
      header: 'Cliente finale',
      cell: (ticket: Ticket) => (
        <span className="text-sm text-[var(--fc-foreground)]">
          {ticket.company?.name ?? '—'}
        </span>
      ),
      className: 'w-44',
    },
    {
      key: 'site',
      header: 'Sede',
      cell: (ticket: Ticket) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {ticket.site?.name ?? '—'}
        </span>
      ),
      className: 'w-40',
    },
    {
      key: 'status',
      header: 'Stato',
      cell: (ticket: Ticket) => <StatusPill status={ticket.status} />,
      className: 'w-36',
    },
    {
      key: 'priority',
      header: 'Priorità',
      cell: (ticket: Ticket) => <PriorityIndicator priority={ticket.priority} />,
      className: 'w-28',
    },
    {
      key: 'sla',
      header: 'SLA',
      cell: (ticket: Ticket) =>
        ticket.slaDeadline ? (
          <SLAIndicator deadline={ticket.slaDeadline} />
        ) : (
          <span className="text-xs text-[var(--fc-foreground-subtle)]">N/D</span>
        ),
      className: 'w-40',
    },
    {
      key: 'assignedTo',
      header: 'Tecnico',
      cell: (ticket: Ticket & { assignedUser?: { firstName: string; lastName: string } }) => (
        <span className="text-sm text-[var(--fc-foreground-muted)]">
          {ticket.assignedUser
            ? `${ticket.assignedUser.firstName} ${ticket.assignedUser.lastName}`.trim()
            : ticket.assignedTo
              ? <span className="font-mono text-xs">{ticket.assignedTo.slice(0, 8)}</span>
              : 'Non assegnato'}
        </span>
      ),
      className: 'w-36',
    },
    {
      key: 'createdAt',
      header: 'Creato',
      cell: (ticket: Ticket) => (
        <span className="text-xs text-[var(--fc-foreground-muted)]">
          {formatRelativeDate(ticket.createdAt)}
        </span>
      ),
      className: 'w-32',
      sortable: true,
    },
  ]

  return (
    <div className="space-y-4">
      <PageHeader
        title={isTechnician ? 'I miei ticket' : 'Ticket'}
        subtitle={data ? `${data.total} ticket${isTechnician ? ' assegnati a te' : ' totali'}` : ''}
        actions={
          <Button
            leftIcon={<Plus className="h-4 w-4" />}
            onClick={() => navigate('/tickets/new')}
          >
            Nuovo ticket
          </Button>
        }
      />

      {/* Filtri */}
      <div className="flex flex-wrap gap-3">
        <div className="flex-1 min-w-[200px]">
          <Input
            placeholder="Cerca per numero, titolo, cliente, sede..."
            leftIcon={<Search className="h-4 w-4" />}
            value={search}
            onChange={(e) => {
              setSearch(e.target.value)
              setPage(1)
            }}
          />
        </div>
        <div className="w-44">
          <Select
            options={STATUS_OPTIONS}
            value={status}
            onValueChange={(v) => { setStatus(v as TicketStatus | ''); setPage(1) }}
            placeholder="Stato"
          />
        </div>
        <div className="w-44">
          <Select
            options={PRIORITY_OPTIONS}
            value={priority}
            onValueChange={(v) => { setPriority(v as TicketPriority | ''); setPage(1) }}
            placeholder="Priorità"
          />
        </div>
        {(status !== 'all' || priority !== 'all' || search) && (
          <Button
            variant="ghost"
            size="md"
            onClick={() => { setSearch(''); setStatus('all'); setPriority('all'); setPage(1) }}
          >
            Azzera filtri
          </Button>
        )}
      </div>

      {/* Tabella */}
      <Table
        data={data?.items ?? []}
        columns={columns}
        keyExtractor={(ticket) => ticket.id}
        isLoading={isLoading}
        onRowClick={(ticket) => navigate(`/tickets/${ticket.id}`)}
        emptyMessage="Nessun ticket trovato"
        rowClassName={(ticket) => ticket.status === 'escalated' ? 'border-l-4 border-l-red-500 bg-red-50/40' : ''}
      />

      {/* Paginazione */}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-[var(--fc-foreground-muted)]">
            Pagina {data.page} di {data.totalPages}
          </p>
          <div className="flex gap-2">
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              Precedente
            </Button>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => setPage((p) => Math.min(data.totalPages, p + 1))}
              disabled={page === data.totalPages}
            >
              Successiva
            </Button>
          </div>
        </div>
      )}

      <ContextualHelp />
    </div>
  )
}
